import { useEffect, useRef, useState } from "react";
import { dateLocale } from "@/lib/i18n";

const DURATION_MS = 700;

/**
 * Zeigt eine Zahl und animiert Wertänderungen wie ein hochlaufender Zähler
 * (Ease-Out über ~0,7s). Beim ersten Rendern wird der Wert direkt gezeigt —
 * nur Änderungen zählen auf. Läuft eine Animation noch, startet die nächste
 * vom aktuell angezeigten Zwischenwert (kein Springen). Respektiert
 * `prefers-reduced-motion`.
 */
export function AnimatedNumber({
  value,
  format,
}: {
  value: number;
  /** Formatter für den Anzeigetext (Default: ganze Zahl, aktive Locale). */
  format?: (n: number) => string;
}) {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = displayRef.current;
    if (from === value) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      displayRef.current = value;
      setDisplay(value);
      return;
    }

    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = t < 1 ? from + (value - from) * eased : value;
      displayRef.current = current;
      setDisplay(current);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value]);

  const fmt = format ?? ((n: number) => Math.round(n).toLocaleString(dateLocale()));
  return <span className="tabular-nums">{fmt(display)}</span>;
}
